import { imageStaticUrl, thumbStaticUrl } from "./assetImage";
import { enqueueImageLoad } from "./imageLoadQueue";
import { relativePathFromAssetUrl } from "./thumb";

const upgraded = new WeakSet<HTMLElement>();

export interface ImageUpgradeHandle {
  /** 断开 observer 并取消所有待处理的图片加载 */
  destroy: () => void;
}

/**
 * Markdown 正文 img：Nginx 静态 URL，先缩略后高清。
 * 使用 IntersectionObserver 懒加载，仅图片进入视口时才请求。
 */
export function upgradeImagesInElement(root: HTMLElement): ImageUpgradeHandle {
  if (upgraded.has(root)) {
    return {
      destroy() {
        /* already upgraded */
      },
    };
  }
  upgraded.add(root);

  let destroyed = false;
  const pendingTimers: ReturnType<typeof setTimeout>[] = [];
  const pendingIdles: number[] = [];

  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img[src]"));

  const io = new IntersectionObserver(
    (entries, observer) => {
      if (destroyed) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const img = entry.target as HTMLImageElement;
        observer.unobserve(img);
        loadProgressive(img);
      }
    },
    { rootMargin: "200px 0px", threshold: 0 }
  );

  imgs.forEach((img) => {
    if (img.dataset.progressive === "1") return;

    const src = img.getAttribute("src") || "";
    let rel = relativePathFromAssetUrl(src);
    if (!rel && src.startsWith("/")) {
      rel = src.replace(/^\/+/, "");
      if (rel.startsWith("api/")) rel = rel.slice(4);
    }
    if (!rel || rel.includes("_缩略图")) return;

    img.dataset.progressive = "1";
    img.dataset.pendingRel = rel;
    img.removeAttribute("src");
    img.classList.add("progressive-image--loading");

    io.observe(img);
  });

  function preloadUrl(url: string): Promise<void> {
    return enqueueImageLoad(
      () =>
        new Promise<void>((resolve, reject) => {
          const probe = new Image();
          probe.onload = () => resolve();
          probe.onerror = () => reject();
          probe.src = url;
        })
    );
  }

  function loadProgressive(img: HTMLImageElement): void {
    if (destroyed) return;
    const rel = img.dataset.pendingRel;
    if (!rel) return;
    delete img.dataset.pendingRel;

    const thumbSrc = thumbStaticUrl(rel);
    const fullSrc = imageStaticUrl(rel);

    preloadUrl(thumbSrc)
      .then(() => {
        if (destroyed || img.dataset.progressive !== "1") return;
        img.src = thumbSrc;

        const runHd = () => {
          if (destroyed) return;
          preloadUrl(fullSrc)
            .then(() => {
              if (destroyed || img.dataset.progressive !== "1") return;
              img.src = fullSrc;
              img.classList.remove("progressive-image--loading");
              img.classList.add("progressive-image--hd");
            })
            .catch(() => {
              if (!destroyed) {
                img.classList.remove("progressive-image--loading");
              }
            });
        };

        if (typeof requestIdleCallback === "function") {
          const id = requestIdleCallback(runHd, { timeout: 4000 });
          pendingIdles.push(id);
        } else {
          const tid = setTimeout(runHd, 400);
          pendingTimers.push(tid);
        }
      })
      .catch(() => {
        if (!destroyed) {
          img.classList.remove("progressive-image--loading");
        }
      });
  }

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      io.disconnect();

      const pendingImgs = root.querySelectorAll<HTMLImageElement>(
        "img[data-pending-rel]"
      );
      pendingImgs.forEach((img) => {
        delete img.dataset.pendingRel;
        delete img.dataset.progressive;
        img.classList.remove("progressive-image--loading");
      });

      pendingTimers.forEach((tid) => clearTimeout(tid));
      pendingTimers.length = 0;
      pendingIdles.forEach((id) => {
        try { cancelIdleCallback(id); } catch { /* ignore */ }
      });
      pendingIdles.length = 0;

      root.querySelectorAll<HTMLImageElement>("img[data-progressive='1']").forEach(
        (img) => {
          delete img.dataset.progressive;
        }
      );

      upgraded.delete(root);
    },
  };
}
